import { Pressable, View } from "react-native"

import { ENV, trpc } from "@src/utils"
import { MenuActions, Text, Image, MCIcon, CollectionPickModal } from "@src/components"
import { ar } from "@src/i18n"
import { Show, useObservable } from "@legendapp/state/react"
import { CollectionCard } from "./collection-card"

type SectionCardProps = {
  section: {
    id: number
    name: string
    status: "active" | "inactive"
    images?: { key: string; thumbhash?: string | null }[]
    collections?: {
      id: number
      name: string
      status: "active" | "inactive"
      productsCount?: number | null
      images?: { key: string; thumbhash?: string | null }[]
    }[]
  }
  onAddCollection: () => void
  onRefresh: () => void
  onEdit: () => void
  onEditCollection: (collection: { id: number; name: string; sectionId: number }) => void
}

export function SectionCard({ section, onAddCollection, onRefresh, onEdit, onEditCollection }: SectionCardProps) {
  const toggleStatus = trpc.menu.toggleSectionStatus.useMutation()
  const deleteSection = trpc.menu.deleteSection.useMutation()
  const linkCollectionsToSection = trpc.menu.linkCollectionsToSection.useMutation()
  const showCollectionPicker$ = useObservable(false)

  const isArchived = section.status === "inactive"
  const sectionImage = section.images?.at(-1)
  const isPending = toggleStatus.isPending || deleteSection.isPending

  const handleToggleStatus = (newStatus: "active" | "inactive") => {
    toggleStatus.mutate({ id: section.id, status: newStatus }, { onSuccess: onRefresh })
  }

  const handleDelete = async () => {
    try {
      await deleteSection.mutateAsync({ id: section.id })
      onRefresh()
      return undefined
    } catch (error) {
      return error instanceof Error ? error.message : "Failed to delete"
    }
  }

  return (
    <View className={`mb-6 bg-white p-4 rounded relative ${isArchived ? "opacity-50" : ""}`}>
      <View className="flex-row items-center justify-between mb-4">
        <View className="flex-row items-center gap-3 flex-1">
          {sectionImage ? (
            <Image
              source={`${ENV.R2_URL}/${sectionImage.key}`}
              style={{ width: 12 * 4, height: 12 * 4, borderRadius: 12 }}
              contentFit="contain"
              loading="lazy"
              recyclingKey={section.id.toString()}
              cachePolicy="memory"
              transition={250}
            />
          ) : (
            <View className="w-12 h-12 bg-gray-200 rounded items-center justify-center">
              <MCIcon name="image-off" size={24} color="#999" />
            </View>
          )}
          <View className="flex-1">
            <Text className="text-lg font-bold">{section.name}</Text>
            {isArchived && (
              <View className="bg-yellow-500 px-2 py-0.5 rounded self-start mt-1">
                <Text className="text-xs text-white font-semibold">{ar.inactive}</Text>
              </View>
            )}
          </View>
        </View>
        <View className="flex-row items-center gap-2">
          <Pressable
            className="bg-blue-500 px-4 py-2 rounded active:opacity-70 disabled:opacity-50"
            onPress={() => showCollectionPicker$.set(true)}
            disabled={linkCollectionsToSection.isPending}
          >
            <MCIcon name={linkCollectionsToSection.isPending ? "refresh" : "link"} size={24} color="white" className={linkCollectionsToSection.isPending ? "animate-spin" : ""} />
          </Pressable>
          <Pressable className="bg-blue-500 px-4 py-2 rounded active:opacity-70" onPress={onAddCollection}>
            <Text className="text-white font-semibold">{ar.addCollection}</Text>
          </Pressable>
        </View>
      </View>

      {/* Collections Grid */}

      {section.collections && section.collections.length > 0 ? (
        <View className="flex-row flex-wrap gap-2">
          {section.collections.map((collection) => (
            <CollectionCard
              key={collection.id.toString()}
              collection={collection}
              onRefresh={onRefresh}
              onEdit={() => onEditCollection({ id: collection.id, name: collection.name, sectionId: section.id })}
            />
          ))}
        </View>
      ) : (
        <Text className="text-gray-500 text-center py-4">{ar.noCollections}</Text>
      )}

      <MenuActions itemName={section.name} status={section.status} onToggleStatus={handleToggleStatus} onDelete={handleDelete} onEdit={onEdit} isPending={isPending} />

      <Show if={showCollectionPicker$}>
        {() => (
          <CollectionPickModal
            value={section.collections?.map((c) => c.id) || []}
            hide={() => showCollectionPicker$.set(false)}
            onChange={(ids) => {
              showCollectionPicker$.set(false)
              linkCollectionsToSection.mutate({ sectionId: section.id, collectionIds: ids }, { onSuccess: onRefresh })
            }}
          />
        )}
      </Show>
    </View>
  )
}
